import { Suspense, lazy, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setPanelCollapsed,
  openDeleteAccessoryPopup,
  openDuplicateAccessoryPopup,
} from 'app/slices/ahuBoardSlice';
import { getCookie } from 'helpers/index';
import type {
  IAhuBoardSection,
  IAhuBoardTunnel,
  IAhuBoardUnit,
  IAhuTunnelLugPlacement,
  ICmsAhuDoc,
  IConstructionClass,
} from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import type { CanvasTunnel } from '../three/canvas3d';
import type { CanvasBootDraws } from '../three/bootDrawFeeds';
import FormsPanel from './FormsPanel';
import CanvasToolbar from './CanvasToolbar';
import ProjectStatus from './ProjectStatus';
import TunnelIndexes from './TunnelIndexes';
import PaletteChart from './PaletteChart';
import SectionToolbarChrome from './SectionToolbarChrome';
import InsertStrip from './InsertStrip';
import InsertSectionPopup, { type InsertPopupRequest } from './InsertSectionPopup';
import DuplicateAccessoryPopup from './DuplicateAccessoryPopup';
import MountingPopup from './MountingPopup';
import FanSpPopup from './FanSpPopup';
import CompTable from './CompTable';
import { ComponentSelectorDialog } from '../forms/selector/ComponentSelectorDialog';

// Route-split (parity 97 / spike risk R5): three r110 + the vendored
// OrbitControls/Tween + Canvas3D live ONLY behind this dynamic import, so the
// dashboard/project-board/login bundles never pay for them. (`import type`
// edges above/below are erased — no static three edge from the main chunk.)
const AhuCanvas = lazy(() => import('./AhuCanvas'));

interface BoardContentProps {
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  /** Tunnel rows + sections + derived current_index_values (R5/6c1). */
  canvasTunnels: CanvasTunnel[];
  /** Per-tunnel section fetches resolved — gates the first full draw (§1.5). */
  sectionsReady: boolean;
  /** Every other `canvasTunnels` feed in — closes AhuCanvas' boot window. */
  canvasDataSettled: boolean;
  /** construction-class floor_construction_id (canvas Y offsets, R6/R15). */
  floorConstructionId: number | null;
  /** 6c2 — G7-G10 lug placements (§20.7 #63 per base_id tunnel); null in flight. */
  lugsByTunnel: Record<number, IAhuTunnelLugPlacement> | null;
  /** 6c2 — boot-draw feed bundle (G11/G12-G21/G31/G32); null in flight. */
  bootDraws: CanvasBootDraws | null;
  /** Selection-restore scoping (`last_selected_sections[0].ahu_id` match, tunnel.js:46). */
  ahuId: number;
  /** 6d — ADP_GAGE canvas-tooltip mfg names (fn['current.cms.adp_gage']). */
  adpGageMfgs: Record<number, string> | null;
  /** 6e1 — panels/windows inputs. */
  cms: ICmsAhuDoc | undefined;
  constructionClasses: IConstructionClass[] | undefined;
  mfgId: number;
  /** Hybrid AHU write gate (KA-x user-skill) — see useAhuPermissions.isPowerUser. */
  isPowerUser: boolean;
  /**
   * Board is view-only *solely* because of the hybrid Standard-user gate — see
   * useAhuPermissions.deriveAhuPermissions. Keeps the Drawing window's Model
   * View settings live while the rest of the board stays locked.
   */
  hybridStandardLock: boolean;
}

/**
 * The children of #ahu-board-content — tmpl/main.tmpl (§2.1), markup faithful
 * (ids/classes/hierarchy are the CSS contract of skeleton.css/styles.css,
 * parity 15). 6a scope: the SHELL — `#canvas_section` is an empty mount the
 * 6b canvas bootstrap takes over; window contents, insert strip, popups and
 * the master table render as their legacy mount nodes.
 *
 * Empty-state contract (render_canvas §3.1, parity 16/17): no first-tunnel
 * length → `#canvas_section.empty` (CSS shows the `.empty-canvas` onboarding
 * card) and palette / canvas toolbar / current-view breadcrumb hidden; first
 * section un-hides them. Legacy uses jQuery hide/show (inline display) —
 * reproduced as inline styles.
 *
 * [NEG]s honored: #14 — the `#airflow_direction` div of skeleton.tmpl is dead
 * (tunnel.js immediately wipes it); the 2D airflow widget is NOT built.
 * #42 — `#insert_component_popup` stays an (unused) anchor; the React insert
 * popup (6e) gets ONE root instead of the legacy duplicate-id append.
 * §4 #15 — commented relics (second model_3d li, `.drawing-canvas` span,
 * #ka-preloader) are not built.
 */
const BoardContent = ({
  ahu,
  tunnels,
  sections,
  canvasTunnels,
  sectionsReady,
  canvasDataSettled,
  floorConstructionId,
  lugsByTunnel,
  bootDraws,
  ahuId,
  adpGageMfgs,
  cms,
  constructionClasses,
  mfgId,
  isPowerUser,
  hybridStandardLock,
}: BoardContentProps) => {
  const dispatch = useAppDispatch();
  const panelCollapsed = useAppSelector((s) => s.ahuBoard.panelCollapsed);
  const currentView = useAppSelector((s) => s.ahuBoard.canvas.view);
  const openWindow = useAppSelector((s) => s.ahuBoard.toolbar.openWindow);
  const canvasReady = useAppSelector((s) => s.ahuBoard.canvas.ready);
  const canvasPainted = useAppSelector((s) => s.ahuBoard.canvas.painted);
  const allowGenerate = useAppSelector((s) => s.ahuValidation.allowGenerate);
  // 6e1 — ONE insert-popup root (ADD + Duplicate modes; [NEG]#42).
  const [insertRequest, setInsertRequest] = useState<InsertPopupRequest | null>(null);

  // render_canvas empty gate: `!tunnels.models[0]?.get('length')` (§3.1).
  // Hybrid AHUs are NOT exempt (requested 2026-10-05, reversing the hold added
  // on 2026-10-02): deleting the last section off one shows the onboarding card
  // like it does on a Standard AHU.
  //
  // Asked of EVERY tunnel rather than just the first (dedrift): legacy's
  // `models[0]` assumes the collection's lead tunnel is the one that grows,
  // which a hybrid AHU breaks — tunnels come back ordered by id (AHUTunnels
  // Meta.ordering), so one empty tunnel sitting at the head of the list hid a
  // unit that had sections in every other one behind the onboarding card. An
  // AHU is empty when NO tunnel has length; that is the condition the card is
  // actually for, and it is identical to the legacy test on a Standard unit.
  const isEmpty = !tunnels.some((t) => t.length);
  const hiddenWhenEmpty = isEmpty ? { display: 'none' } : undefined;

  /**
   * §2.4 changeMainRotationWidth — verbatim port of the inline-CSS shifts
   * (view/main.js:218): widgets that sit relative to the collapsed left panel.
   * Some targets only exist in later sub-phases (.delete-duplicate-wrapper,
   * .rating-mode-active) — querySelectorAll is naturally a no-op for them.
   */
  const onAhuShyToggle = (checked: boolean) => {
    dispatch(setPanelCollapsed(checked));
    const css = (selector: string, styles: Partial<CSSStyleDeclaration> | null) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        if (styles === null) el.removeAttribute('style');
        else Object.assign(el.style, styles);
      });
    };
    if (checked) {
      css('.error-log-wrapper', { right: '78px' });
      css('.btn-generate', { right: '75px' });
      css('.delete-duplicate-wrapper', { right: '76px' });
      css('.rating-mode-active.main-rotation', { width: 'calc(100% - 503px)' });
      css('.rating-mode-active.main-rotation .performance-group', { paddingLeft: '30px' });
    } else {
      css('.error-log-wrapper', null);
      css('.btn-generate', null);
      css('.delete-duplicate-wrapper', null);
      css('.rating-mode-active.main-rotation', { width: 'calc(100% - 480px)' });
      css('.rating-mode-active.main-rotation .performance-group', { paddingLeft: '15px' });
    }
  };

  /**
   * 6d — context-menu item actions (MainView proxies, view/main.js:837-871;
   * the canvas stamps data-id/data-type/data-section when opening, §3.2.7 /
   * parity 71-72):
   *  - SECTION Duplicate/Delete → `show:duplicate:popup` / `show:delete:popup`
   *    bus events — same flows as the toolbar lis; receiver =
   *    ViewSectionToolbar port. TODO(6e §8.5/parity 162): popup consumer.
   *  - ACCESSORY Duplicate/Delete → §13 row 12 store actions
   *    (openDuplicateAccessoryPopup / openDeleteAccessoryPopup) replacing the
   *    `a.accessory_duplicate` / `a.accessory_remove` click proxies.
   *    TODO(6e §13 row 12): DuplicateAccessory (§12.1) + per-row delete
   *    (§12.3) popups subscribe to popupSeams.
   * Menu hiding mirrors `$('.context-menu').addClass('hide')`; the canvas's
   * window-click closeContextMenu also fires on the same click (legacy too).
   */
  const closeContextMenus = () => {
    document.querySelectorAll('.context-menu').forEach((m) => m.classList.add('hide'));
  };
  const menuDataId = (e: React.MouseEvent<HTMLButtonElement>): number | null => {
    const raw = e.currentTarget.getAttribute('data-id');
    const id = raw == null ? NaN : Number(raw);
    return Number.isFinite(id) ? id : null;
  };

  return (
    <>
      {/* main.tmpl's own (2nd) preloader — RE-ARMED in 6b (the 6a build hid it
          because nothing collapsed it). Legacy removes the node in the
          FontLoader callback (fn/map.js §1.7); here it stays up until the
          canvas has booted (font + context, parity 7) AND drawn the unit's
          sections (`canvas.painted` — AhuCanvas's boot-paint gate).
          Collapsing it on `ready` alone handed over an empty canvas that the
          sections then appeared on seconds later (reported 2026-10-15). */}
      {(!canvasReady || !canvasPainted) && <div className="preloader dotted-preloader" />}
      {/* QA5 #4 — main.tmpl separates these top-level siblings with newlines;
          the rendered whitespace indents the inline-block
          #forms-main-container by one space (measured 4 px on dev3). Keep
          the text nodes or the whole Data Bar sits 4 px left of legacy. */}
      <div id="main" />{' '}
      <div className="transparent-layer" />{' '}
      <ProjectStatus projectId={ahu.project_id} />{' '}
      <FormsPanel
        ahu={ahu}
        tunnels={tunnels}
        sections={sections}
        cms={cms}
        constructionClasses={constructionClasses}
        mfgId={mfgId}
        isPowerUser={isPowerUser}
      />{' '}
      <div className="hover-tooltip">
        <div id="filter_tooltip" />
        <div id="eliminator_tooltip" />
        <div id="evap_pack_tooltip" />
        <div id="gas_heater_tooltip" />
      </div>

      {/* Hamburger: panel collapse is pure CSS off the checkbox (parity 19). */}
      <input
        id="ahu_shy"
        className="ahu-shy-btn"
        type="checkbox"
        tabIndex={-1}
        checked={panelCollapsed}
        onChange={(e) => onAhuShyToggle(e.target.checked)}
      />
      <label htmlFor="ahu_shy" className="icon-hamburger" />

      {/* Sections/Accessories master-strip flip — CSS-only radios (parity 18/178). */}
      <input
        id="components"
        type="radio"
        name="ahu_components"
        tabIndex={-1}
        className="toolbar-ctrl-input hidden"
        defaultChecked
      />
      <input
        id="accessories"
        type="radio"
        name="ahu_components"
        tabIndex={-1}
        className="toolbar-ctrl-input hidden"
      />
      <div className="toolbar-ctrl-labels">
        <label
          htmlFor="components"
          className="toolbar-ctrl-label icon-sections-icon"
          tooltip="Sections"
        >
          {/* Second load-bearing space, INSIDE the label: main.tmpl:21-23 opens
              the label, breaks the line, then `<span>`. The icon is the label's
              `::before`, so that newline collapses to a word-space BETWEEN glyph
              and text — the same 3-4px gap the icon rows below use. Without it
              each label is ~3.4px narrower and, the box being right-anchored,
              SECTIONS slid right out of line with its icon column. */}{' '}
          <span>Sections</span>
        </label>
        {/* Literal space is load-bearing: both labels are inline-block in a
            shrink-to-fit, right:10px absolutely-positioned box, and
            `.toolbar-ctrl-label { margin-right: -4px }`
            (ahu/styles.css:9564-9588, source _ui.section_accss_group.scss:136)
            exists purely to cancel the ~4px inter-element word-space legacy's
            main.tmpl:22-28 had between the two <label>s. JSX drops that
            whitespace, so the box got 4px narrower and — anchored on its right
            edge — SECTIONS slid 4px right, out of line with its icon column
            while ACCESSORIES stayed put. */}{' '}
        <label
          htmlFor="accessories"
          className="toolbar-ctrl-label icon-accessories-icon"
          tooltip="Accessories"
        >
          {/* main.tmpl:24-27 — same glyph/text word-space as Sections. */}{' '}
          <span>Accessories</span>
        </label>
      </div>

      <div className="main-rotation">
        <div className="rotate-card">
          <div className="main-section">
            <div className="canvas-window">
              {/* ViewCanvasTunnel el — the 6b canvas (canvas#ahu_canvas +
                  renderer) mounts here; context-loss reset re-keys the canvas
                  inside (§1.7). `.empty` keeps the onboarding card CSS
                  (parity 16) — legacy also constructs the canvas when empty. */}
              <div
                id="canvas_section"
                className={isEmpty ? 'empty' : undefined}
                style={{ position: 'relative' }}
              >
                <Suspense fallback={null}>
                  <AhuCanvas
                    tunnels={canvasTunnels}
                    isEmpty={isEmpty}
                    sectionsReady={sectionsReady}
                    canvasDataSettled={canvasDataSettled}
                    floorConstructionId={floorConstructionId}
                    lugsByTunnel={lugsByTunnel}
                    bootDraws={bootDraws}
                    ahuId={ahuId}
                    adpGageMfgs={adpGageMfgs}
                  />
                </Suspense>
                {/* Hybrid AHU (KA-x) — main.js's _renderHybridBanner, restyled
                    per the later legacy commit (top:15/left:85, weight 300,
                    opacity .7, 13px) rather than its original bolder version.
                    The read-only LOCK itself is the ahu-view-permission body
                    class below, not this banner — it's copy only. */}
                {ahu.is_hybrid && !isPowerUser && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 15,
                      left: 85,
                      color: '#e53935',
                      fontWeight: 300,
                      opacity: 0.7,
                      fontSize: 13,
                    }}
                  >
                    This Air Handling has a Hybrid configuration and as such, you may not
                    edit it as a Standard user
                  </div>
                )}
              </div>
              <div className="empty-canvas">
                <div className="ahu-img" />
                <div>
                  <span className="heading">Build your Air Handling Unit</span>
                  <ul start={1}>
                    <li>
                      <span>1.</span> Add a Section (Component)
                      <i className="icon-sections " />
                      <span className="small-text">Double-click in the right menu</span>
                    </li>
                    <li>
                      <span>2.</span> Select a Section
                      <span className="small-text">Click on Section in canvas</span>
                    </li>
                    <li>
                      <span>3.</span> Add an Accessory
                      <i className="icon-accessories-1" />
                      <span className="small-text"> Double-click in the right menu</span>
                    </li>
                    <li>
                      <span>4.</span> Edit Parameters
                      <span className="small-text">Select tab and group in the left bar</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <input
              id="ahu_shy_dwg"
              className="ahu-shy-btn input_toolbar input_title_block"
              type="checkbox"
              tabIndex={-1}
              checked={openWindow === 'title_block'}
              readOnly
            />
            <input
              id="ahu_shy_dwg_1"
              className="ahu-shy-btn input_toolbar input_auto"
              type="checkbox"
              tabIndex={-1}
              readOnly
              checked={false}
            />
            {/* CAD generate form — POST /model/generate/ (§20.10 #105).
                Gating per §8.1 changeGenerateButton; the click also fires the
                §20.10 #106 generate_data analytics POST ([NEG]#52 — live
                usage-stat side effect, kept). */}
            <form id="cad_generate" action="/model/generate/" method="post" target="_blank">
              <input type="hidden" name="id" value={ahu.id} />
              <input
                type="hidden"
                name="csrfmiddlewaretoken"
                value={getCookie('csrftoken') ?? ''}
              />
              <button
                className={`hide btn btn-generate ka-has-tooltip ${
                  allowGenerate ? 'enable-generate' : 'disable-generate'
                }`}
                {...(allowGenerate ? { tooltip: 'Generate 3D model' } : {})}
                id="ahu3dGenerate"
                tabIndex={-1}
                type={allowGenerate ? 'submit' : 'button'}
                onClick={(e) => {
                  if (!allowGenerate) {
                    e.preventDefault();
                    return;
                  }
                  // §20.10 #106 — ModelsGenerated usage stat (response ignored).
                  const body = new FormData();
                  body.append('id', String(ahu.id));
                  body.append('csrfmiddlewaretoken', getCookie('csrftoken') ?? '');
                  void fetch('/model/generate_data/', {
                    method: 'POST',
                    credentials: 'same-origin',
                    body,
                  }).catch(() => undefined);
                }}
              />
            </form>

            <CanvasToolbar
              ahuId={ahu.id}
              tunnelsCount={tunnels.length}
              hiddenForEmptyState={isEmpty}
              tunnels={tunnels}
              sections={sections}
              hybridStandardLock={hybridStandardLock}
              ahuIsHybrid={ahu.is_hybrid}
            />

            <div id="canvas_notes">
              <span className="canvas-notes">
                {/* Requested 2026-09-30 — was "Certain details omitted for
                    clarity."; the note is about components specifically. */}
                Certain Component details omitted for clarity.
                <span />
              </span>
            </div>
            <span className="messages_wrapper">
              <span className="canvas-tunnel-loading">
                loading...<span>,</span>
              </span>
              <span className="error-log-validation">
                validating...<span>,</span>
              </span>
              <span className="model-view-generating">
                generating...<span>,</span>
              </span>
              <span className="added-section-note">
                adding section...<span>,</span>
              </span>
              <span className="duplicated-accss-note">
                duplicating accessory...<span>,</span>
              </span>
              <span className="added-accss-note">
                adding accessory...<span>,</span>
              </span>
              <span className="added-drain-pan-note">
                adding drain pan...<span>,</span>
              </span>
              <span className="deleting-section-note">
                deleting section...<span>,</span>
              </span>
              <span className="duplicating-section-note">
                duplicating section...<span>,</span>
              </span>
            </span>
            <span id="accessory-tooltip-for-canvas" className="hide" />
            <div id="accessory-context-menu" className="context-menu hide">
              <div className="heading" />
              <button
                tooltip="Duplicate"
                name="duplicate"
                className="ka-tooltip-show-after icon-copy"
                onClick={(e) => {
                  const id = menuDataId(e);
                  closeContextMenus();
                  if (id != null) dispatch(openDuplicateAccessoryPopup(id));
                }}
              >
                Duplicate
              </button>
              <button
                tooltip="Delete"
                name="delete"
                className="  ka-tooltip-show-after icon-trash"
                onClick={(e) => {
                  const id = menuDataId(e);
                  closeContextMenus();
                  if (id != null) dispatch(openDeleteAccessoryPopup(id));
                }}
              >
                Delete
              </button>
            </div>
            <div id="section-context-menu" className="context-menu hide">
              <div className="heading" />
              <button
                tooltip="Duplicate"
                name="duplicate"
                className="ka-tooltip-show-after icon-copy"
                onClick={(e) => {
                  const id = menuDataId(e);
                  if (id != null) ahuBoardBus.emit('show:duplicate:popup', id);
                  closeContextMenus();
                }}
              >
                Duplicate
              </button>
              <button
                tooltip="Delete"
                name="delete"
                className="  ka-tooltip-show-after icon-trash"
                onClick={(e) => {
                  const id = menuDataId(e);
                  if (id != null) ahuBoardBus.emit('show:delete:popup', null, id);
                  closeContextMenus();
                }}
              >
                Delete
              </button>
            </div>
            <div id="resolved-errors-information">
              <div className="resolved-errors-information-row errors-row">
                <div>Errors</div>
                <div>
                  <span className="errors_resolved" /> resolved
                </div>
                <div>/</div>
                <div>
                  <span className="errors_unresolved" /> unresolved
                </div>
              </div>
              <div className="resolved-errors-information-row warnings-row">
                <div>Warnings</div>
                <div>
                  <span className="warnings_resolved" /> resolved
                </div>
                <div>/</div>
                <div>
                  <span className="warnings_unresolved" /> unresolved
                </div>
              </div>
            </div>

            {/* Current-view breadcrumb — main.tmpl order (back_right BEFORE
                back_left; differs from the views list — keep both as-is, §2.2). */}
            <div id="canvas-current-view" style={hiddenWhenEmpty}>
              {(
                [
                  ['default', 'Isometric Front Right'],
                  ['front_left', 'Isometric Front Left'],
                  ['back_right', 'Isometric Back Right'],
                  ['back_left', 'Isometric Back Left'],
                  ['plan', 'Plan'],
                  ['elevation_near_side', 'Elevation Near Side'],
                ] as const
              ).map(([value, label]) => (
                <span
                  key={value}
                  className={`${value}${currentView === value ? ' active' : ''}`}
                  data-value={value}
                >
                  {label}
                </span>
              ))}
            </div>
            {/* Tunnel-index pills (§2.3) — canvas-driven via the slice
                (renderTunnelsIndexes re-home, 6b); 'all' active by default. */}
            <TunnelIndexes tunnels={tunnels} isHybrid={ahu.is_hybrid} sections={sections} />
          </div>
          {/* CompTable — "dark side" of the card flip (§11.1). */}
          <div id="component_table" className="comp-table">
            <CompTable />
          </div>
          {/* ComponentSelector embed host (§30.1 — 6e2c chassis). */}
          <div id="component_rating_mode" className="rating-mode-wrapper">
            <ComponentSelectorDialog ahu={ahu} />
          </div>
        </div>
      </div>

      <div id="pallete-wrapper" style={hiddenWhenEmpty}>
        {/* PaletteView port (§2.5/§11.2) — color-mode toggle wired to the
            slice + `change_canvas_color_mode` bus channel (6b). */}
        <PaletteChart />
      </div>

      {/* Insert strip — ViewToolbarComponents (§9.1). */}
      <div id="components_section">
        <InsertStrip
          tunnels={tunnels}
          sections={sections}
          isPowerUser={isPowerUser}
          onOpenInsertPopup={({ name, value, type }) =>
            setInsertRequest({ name, value, type })
          }
        />
      </div>

      {/* ONE insert-popup root — ADD + Duplicate modes ([NEG]#42). */}
      <div id="insert_component_popup">
        {insertRequest && (
          <InsertSectionPopup
            request={insertRequest}
            ahu={ahu}
            tunnels={tunnels}
            sections={sections}
            cms={cms}
            isPowerUser={isPowerUser}
            onClose={() => setInsertRequest(null)}
          />
        )}
      </div>
      {/* ViewSectionToolbar (§8.5). */}
      <div id="canvas_section_toolbar">
        <SectionToolbarChrome
          ahuId={ahu.id}
          tunnels={tunnels}
          sections={sections}
          isHybrid={ahu.is_hybrid}
          onDuplicateSection={(section) => {
            // §8.5 duplicate_section → insert popup in duplicate mode; type
            // resolved from the matching insert-toolbar label (single here —
            // dual-tunnel sections are blocked upstream).
            setInsertRequest({
              name: String(section.name ?? ''),
              value: String(section.type ?? ''),
              type: 'single_tunnel',
              duplicate: true,
              sectionId: section.id,
            });
          }}
        />
      </div>
      {/* FanSPPopupView el (§12.2). */}
      <div id="fan-sp-popup-wrapper">
        <FanSpPopup />
      </div>
      {/* DuplicateAccessory popup host (§12.1 — §13 row 12 seam consumer). */}
      <div className="insert_accessory_popup_all" style={{ border: 'none' }}>
        <DuplicateAccessoryPopup tunnels={tunnels} sections={sections} />
      </div>
      {/* Mounting popup host (§13 row 10 seam consumer; engine = 6e2 §51.4). */}
      <MountingPopup />

      {/* Delete Accessory popup (main.tmpl inline block — flow lands in 6e). */}
      <div id="delete-acc-popup" className="popup-wrapper delete-accessory-popup width-small">
        <div className="popup-content">
          <h2 className="popup-heading">Delete Accessory?</h2>
          <div>
            <div className="clearfix popup-content-part">
              <p>
                If you choose to Delete, all Accessory Data will be lost and will not be
                recoverable.
              </p>
              <p>
                Section where Accessory is located: <span className="section-name-or-tag" />
              </p>
              <span className="section-tag-value" />
            </div>
            <div className="popup-control-buttons">
              <button className="delete not-highlighted-btn" name="delete_accessory_btn">
                DELETE
              </button>
              <button className="default-btn highlighted-btn" name="cancel_delete_accessory_btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BoardContent;
